<template>
  <div class="plan-page">
    <section class="plan-stats">
      <button
        v-for="item in statCards"
        :key="item.key"
        type="button"
        class="plan-stat"
        :class="`plan-stat--${item.tone}`"
        @click="applyStatusCategory(item.statusCategory)"
      >
        <span>{{ item.label }}</span>
        <strong>{{ formatNumber(item.value) }}</strong>
        <small>{{ item.hint }}</small>
      </button>
    </section>

    <section class="plan-card">
      <div class="plan-type-tabs">
        <button
          v-for="tab in typeTabs"
          :key="tab.value || 'ALL'"
          type="button"
          :class="{ active: filters.type === tab.value }"
          @click="changeType(tab.value)"
        >
          <span>{{ tab.label }}</span>
          <em>{{ tab.count }}</em>
        </button>
      </div>

      <div class="plan-toolbar">
        <div class="plan-toolbar__copy">
          <strong>{{ currentTypeLabel }}</strong>
          <span>共 {{ total }} 个计划</span>
        </div>
        <button class="ghost-button" type="button" :disabled="loading" @click="refreshAll">
          {{ loading ? "刷新中" : "刷新" }}
        </button>
      </div>

      <div class="plan-filters">
        <input
          v-model.trim="filters.keyword"
          type="search"
          placeholder="搜索计划、宠物、用户或说明"
          @keyup.enter="search"
        />
        <select v-model="filters.statusCategory" @change="search">
          <option value="">全部状态</option>
          <option value="ACTIVE">进行中</option>
          <option value="PAUSED">已暂停</option>
          <option value="COMPLETED">已完成</option>
        </select>
        <button class="primary-button" type="button" @click="search">搜索</button>
      </div>

      <div v-if="loading" class="plan-state">正在加载计划…</div>
      <div v-else-if="!rows.length" class="plan-state">
        <strong>没有匹配的计划</strong>
        <span>调整计划类型、状态或搜索条件后再试。</span>
      </div>
      <div v-else class="plan-grid">
        <button
          v-for="item in rows"
          :key="item.key"
          type="button"
          class="plan-item"
          :class="{ 'plan-item--due': isDue(item), 'plan-item--overdue': isOverdue(item) }"
          @click="openDetail(item)"
        >
          <span class="plan-item__top">
            <span class="plan-type">{{ item.planTypeLabel }}</span>
            <span class="plan-status" :class="`plan-status--${item.statusCategory.toLowerCase()}`">
              {{ item.statusLabel }}
            </span>
          </span>
          <strong class="plan-item__title">{{ item.title }}</strong>
          <span class="plan-owner">{{ item.petName || "未关联宠物" }} · {{ item.ownerNickname || "用户" }}</span>

          <span class="plan-meta">
            <span>
              <small>计划时间</small>
              <b>{{ rangeText(item) }}</b>
            </span>
            <span>
              <small>下次执行</small>
              <b :class="{ danger: isOverdue(item), due: isDue(item) }">{{ item.nextActionDate || "未安排" }}</b>
            </span>
          </span>

          <span v-if="item.totalItems > 0" class="plan-progress">
            <span class="plan-progress__head">
              <small>完成进度</small>
              <b>{{ item.completedItems }}/{{ item.totalItems }}</b>
            </span>
            <i><span :style="{ width: `${item.progressPercent}%` }"></span></i>
          </span>
          <span v-else class="plan-progress plan-progress--empty">{{ sourceLabel(item.sourceType) }}</span>

          <span class="plan-description">{{ item.description || "暂无计划说明" }}</span>
          <span class="plan-open">查看计划详情 ›</span>
        </button>
      </div>

      <div v-if="total > pageSize" class="pager">
        <button class="ghost-button" type="button" :disabled="page <= 1" @click="changePage(page - 1)">上一页</button>
        <span class="pager-text">第 {{ page }} / {{ totalPages }} 页 · 共 {{ total }} 条</span>
        <button class="ghost-button" type="button" :disabled="page >= totalPages" @click="changePage(page + 1)">下一页</button>
      </div>
    </section>

    <div v-if="selectedPlan" class="plan-detail-mask" @click.self="closeDetail">
      <article class="plan-detail">
        <header>
          <button class="plan-detail__back" type="button" @click="closeDetail">‹ 返回计划列表</button>
          <span class="plan-type">{{ selectedPlan.planTypeLabel }}</span>
          <h2>{{ selectedPlan.title }}</h2>
          <span class="plan-status" :class="`plan-status--${selectedPlan.statusCategory.toLowerCase()}`">
            {{ selectedPlan.statusLabel }}
          </span>
        </header>
        <div class="plan-detail__body">
          <dl>
            <div><dt>宠物</dt><dd>{{ selectedPlan.petName || "未关联宠物" }}</dd></div>
            <div><dt>用户</dt><dd>{{ selectedPlan.ownerNickname || "用户" }}（UID {{ selectedPlan.ownerUserId }}）</dd></div>
            <div><dt>计划开始</dt><dd>{{ selectedPlan.startDate || "未设置" }}</dd></div>
            <div><dt>计划结束</dt><dd>{{ selectedPlan.endDate || "长期 / 未设置" }}</dd></div>
            <div><dt>下次执行</dt><dd>{{ selectedPlan.nextActionDate || "未安排" }}</dd></div>
            <div><dt>计划来源</dt><dd>{{ sourceLabel(selectedPlan.sourceType) }}</dd></div>
            <div v-if="selectedPlan.totalItems > 0"><dt>完成进度</dt><dd>{{ selectedPlan.completedItems }}/{{ selectedPlan.totalItems }}（{{ selectedPlan.progressPercent }}%）</dd></div>
            <div><dt>更新时间</dt><dd>{{ formatTime(selectedPlan.updatedAt) }}</dd></div>
          </dl>
          <section>
            <h3>计划说明</h3>
            <p>{{ selectedPlan.description || "暂无计划说明" }}</p>
          </section>
          <div v-if="selectedPlan.totalItems > 0" class="plan-detail__progress">
            <i><span :style="{ width: `${selectedPlan.progressPercent}%` }"></span></i>
          </div>
        </div>
        <footer>
          <button class="primary-button" type="button" @click="closeDetail">关闭</button>
        </footer>
      </article>
    </div>
  </div>
</template>

<script>
import { fetchAdminPlanOverview, fetchAdminPlans } from "../api/planAdmin"

const emptyStats = () => ({
  totalPlans: 0,
  activePlans: 0,
  pausedPlans: 0,
  completedPlans: 0,
  planUsers: 0,
  activePlanUsers: 0,
  totalUsers: 0,
  planUserCoverageRate: 0,
  duePlans: 0,
  overduePlans: 0,
  feedingPlans: 0,
  nutritionPlans: 0,
  vaccinePlans: 0,
  dewormingPlans: 0,
  breedingPlans: 0
})

export default {
  name: "PlanManagementView",
  data() {
    return {
      loading: false,
      loadingStats: false,
      rows: [],
      total: 0,
      page: 1,
      pageSize: 20,
      stats: emptyStats(),
      selectedPlan: null,
      filters: { type: "", statusCategory: "", keyword: "" }
    }
  },
  computed: {
    totalPages() {
      return Math.max(1, Math.ceil((this.total || 0) / this.pageSize))
    },
    statCards() {
      return [
        { key: "total", label: "全部计划", value: this.stats.totalPlans, hint: "覆盖所有计划类型", tone: "blue", statusCategory: "" },
        { key: "users", label: "计划用户", value: this.stats.planUsers, hint: `平台用户覆盖率 ${this.stats.planUserCoverageRate}%`, tone: "purple", statusCategory: "" },
        { key: "active", label: "进行中", value: this.stats.activePlans, hint: "正在执行或待继续", tone: "green", statusCategory: "ACTIVE" },
        { key: "due", label: "近期待办", value: this.stats.duePlans, hint: "今天及已到期", tone: "amber", statusCategory: "ACTIVE" },
        { key: "overdue", label: "已逾期", value: this.stats.overduePlans, hint: "需要优先跟进", tone: "rose", statusCategory: "ACTIVE" },
        { key: "completed", label: "已完成计划", value: this.stats.completedPlans, hint: "已结束执行的计划", tone: "slate", statusCategory: "COMPLETED" }
      ]
    },
    typeTabs() {
      return [
        { value: "", label: "全部计划", count: this.stats.totalPlans },
        { value: "FEEDING_SCHEDULE", label: "喂食日历", count: this.stats.feedingPlans },
        { value: "FEEDING_NUTRITION", label: "营养喂食", count: this.stats.nutritionPlans },
        { value: "VACCINE", label: "疫苗", count: this.stats.vaccinePlans },
        { value: "DEWORMING", label: "驱虫", count: this.stats.dewormingPlans },
        { value: "BREEDING", label: "繁育", count: this.stats.breedingPlans }
      ]
    },
    currentTypeLabel() {
      return this.typeTabs.find(item => item.value === this.filters.type)?.label || "全部计划"
    }
  },
  created() {
    this.refreshAll()
  },
  methods: {
    async refreshAll() {
      await Promise.all([this.loadOverview(), this.loadPlans()])
    },
    async loadOverview() {
      if (this.loadingStats) return
      this.loadingStats = true
      try {
        const res = await fetchAdminPlanOverview()
        this.stats = { ...emptyStats(), ...(res.data || {}) }
      } finally {
        this.loadingStats = false
      }
    },
    async loadPlans() {
      if (this.loading) return
      this.loading = true
      try {
        const res = await fetchAdminPlans({
          type: this.filters.type || undefined,
          statusCategory: this.filters.statusCategory || undefined,
          keyword: this.filters.keyword || undefined,
          page: this.page,
          pageSize: this.pageSize
        })
        const data = res.data || {}
        this.rows = Array.isArray(data.records) ? data.records : []
        this.total = Number(data.total || 0)
      } finally {
        this.loading = false
      }
    },
    changeType(type) {
      this.filters.type = type
      this.page = 1
      this.loadPlans()
    },
    applyStatusCategory(value) {
      this.filters.statusCategory = value
      this.page = 1
      this.loadPlans()
    },
    search() {
      this.page = 1
      this.loadPlans()
    },
    changePage(next) {
      this.page = Math.min(Math.max(1, next), this.totalPages)
      this.loadPlans()
    },
    openDetail(item) {
      this.selectedPlan = item
    },
    closeDetail() {
      this.selectedPlan = null
    },
    isOverdue(item) {
      return item.statusCategory === "ACTIVE" && item.nextActionDate && item.nextActionDate < this.today()
    },
    isDue(item) {
      return item.statusCategory === "ACTIVE" && item.nextActionDate && item.nextActionDate <= this.today()
    },
    today() {
      const date = new Date()
      const month = String(date.getMonth() + 1).padStart(2, "0")
      const day = String(date.getDate()).padStart(2, "0")
      return `${date.getFullYear()}-${month}-${day}`
    },
    rangeText(item) {
      if (!item.startDate && !item.endDate) return "未设置"
      if (!item.endDate) return `${item.startDate || "-"} 起`
      return `${item.startDate || "-"} 至 ${item.endDate}`
    },
    sourceLabel(source) {
      if (source === "AI") return "AI 生成"
      if (source === "CUSTOM") return "用户自定义"
      return "手动配置"
    },
    formatNumber(value) {
      return Number(value || 0).toLocaleString("zh-CN")
    },
    formatTime(value) {
      return value ? String(value).replace("T", " ").slice(0, 16) : "-"
    }
  }
}
</script>

<style scoped>
.plan-page {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.plan-stats {
  display: grid;
  grid-template-columns: repeat(6, minmax(0, 1fr));
  gap: 12px;
}

.plan-stat {
  min-width: 0;
  padding: 16px;
  border: 1px solid #e6ebf1;
  border-radius: 15px;
  background: #ffffff;
  text-align: left;
  cursor: pointer;
}

.plan-stat span,
.plan-stat small {
  display: block;
  color: #7c8996;
  font-size: 11px;
}

.plan-stat strong {
  display: block;
  margin: 10px 0 7px;
  color: #17202b;
  font-size: 25px;
}

.plan-stat--green strong { color: #16784a; }
.plan-stat--purple strong { color: #7450a8; }
.plan-stat--amber strong { color: #b16b00; }
.plan-stat--rose strong { color: #b44332; }
.plan-stat--slate strong { color: #586575; }

.plan-card {
  padding: 18px;
  border: 1px solid #e8edf3;
  border-radius: 17px;
  background: #ffffff;
}

.plan-type-tabs {
  display: flex;
  gap: 6px;
  padding: 4px;
  overflow-x: auto;
  border-radius: 12px;
  background: #f3f6f8;
  scrollbar-width: none;
}

.plan-type-tabs::-webkit-scrollbar { display: none; }

.plan-type-tabs button {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  flex: 0 0 auto;
  min-height: 34px;
  padding: 0 12px;
  border: 0;
  border-radius: 9px;
  background: transparent;
  color: #64748b;
  font-size: 12px;
  cursor: pointer;
}

.plan-type-tabs button.active {
  background: #ffffff;
  color: #17202b;
  font-weight: 700;
  box-shadow: 0 2px 8px rgba(15, 23, 42, 0.08);
}

.plan-type-tabs em {
  min-width: 20px;
  padding: 1px 6px;
  border-radius: 999px;
  background: #e8edf3;
  font-size: 10px;
  font-style: normal;
  text-align: center;
}

.plan-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 14px;
  margin-top: 14px;
}

.plan-toolbar__copy {
  display: flex;
  align-items: baseline;
  gap: 9px;
}

.plan-toolbar__copy strong { font-size: 15px; }
.plan-toolbar__copy span { color: #94a3b8; font-size: 11px; }

.ghost-button,
.primary-button {
  min-height: 36px;
  padding: 0 14px;
  border-radius: 9px;
  font-size: 12px;
  cursor: pointer;
}

.ghost-button {
  border: 1px solid #dce3ea;
  background: #ffffff;
  color: #465464;
}

.primary-button {
  border: 0;
  background: #182230;
  color: #ffffff;
}

.plan-filters {
  display: flex;
  gap: 9px;
  margin: 12px 0 16px;
}

.plan-filters input,
.plan-filters select {
  height: 38px;
  padding: 0 11px;
  border: 1px solid #dce3ea;
  border-radius: 9px;
  background: #ffffff;
  color: #334155;
  font-size: 12px;
}

.plan-filters input { flex: 1; min-width: 0; }
.plan-filters select { width: 135px; }

.plan-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 12px;
}

.plan-item {
  display: flex;
  flex-direction: column;
  min-width: 0;
  padding: 15px;
  border: 1px solid #e7ecf2;
  border-radius: 14px;
  background: #ffffff;
  color: #26323e;
  text-align: left;
  cursor: pointer;
}

.plan-item:hover {
  border-color: #cfd8e3;
  box-shadow: 0 10px 26px rgba(15, 23, 42, 0.07);
}

.plan-item--due { border-left: 3px solid #e0a21b; }
.plan-item--overdue { border-left: 3px solid #cc5a45; }

.plan-item__top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}

.plan-type {
  padding: 3px 8px;
  border-radius: 999px;
  background: #edf3ff;
  color: #315ea8;
  font-size: 10px;
  font-weight: 700;
}

.plan-status {
  padding: 3px 8px;
  border-radius: 999px;
  background: #edf7f1;
  color: #19734a;
  font-size: 10px;
  white-space: nowrap;
}

.plan-status--paused { background: #fff5df; color: #a26700; }
.plan-status--completed { background: #eef2f6; color: #677483; }

.plan-item__title {
  margin-top: 10px;
  overflow: hidden;
  font-size: 15px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.plan-owner {
  margin-top: 5px;
  color: #758392;
  font-size: 11px;
}

.plan-meta {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
  margin-top: 13px;
}

.plan-meta span,
.plan-meta small,
.plan-meta b {
  display: block;
}

.plan-meta small { color: #94a3b8; font-size: 10px; }
.plan-meta b { margin-top: 4px; color: #3d4b59; font-size: 11px; font-weight: 600; }
.plan-meta b.due { color: #a26700; }
.plan-meta b.danger { color: #b44332; }

.plan-progress {
  display: block;
  margin-top: 13px;
}

.plan-progress__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  color: #8a96a3;
  font-size: 10px;
}

.plan-progress > i,
.plan-detail__progress i {
  display: block;
  height: 6px;
  margin-top: 7px;
  overflow: hidden;
  border-radius: 999px;
  background: #edf1f5;
}

.plan-progress > i span,
.plan-detail__progress i span {
  display: block;
  height: 100%;
  border-radius: inherit;
  background: #3a8b65;
}

.plan-progress--empty {
  color: #8a96a3;
  font-size: 10px;
}

.plan-description {
  display: -webkit-box;
  min-height: 34px;
  margin-top: 12px;
  overflow: hidden;
  color: #6d7a87;
  font-size: 11px;
  line-height: 17px;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
}

.plan-open {
  margin-top: 12px;
  color: #246c50;
  font-size: 11px;
  text-align: right;
}

.plan-state {
  display: flex;
  align-items: center;
  flex-direction: column;
  gap: 5px;
  padding: 46px 16px;
  color: #8a96a3;
  font-size: 12px;
}

.pager {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 16px;
}

.pager-text { color: #7b8794; font-size: 11px; }

.plan-detail-mask {
  position: fixed;
  inset: 0;
  z-index: 100;
  display: grid;
  place-items: center;
  padding: 16px;
  background: rgba(15, 23, 42, 0.42);
}

.plan-detail {
  display: flex;
  flex-direction: column;
  width: min(620px, 100%);
  max-height: calc(100dvh - 32px);
  overflow: hidden;
  border-radius: 18px;
  background: #ffffff;
}

.plan-detail header {
  display: flex;
  align-items: center;
  gap: 9px;
  flex-wrap: wrap;
  padding: 18px 20px;
  border-bottom: 1px solid #edf0f3;
}

.plan-detail header h2 {
  flex: 1;
  min-width: 180px;
  margin: 0;
  font-size: 18px;
}

.plan-detail__back {
  display: none;
}

.plan-detail__body {
  padding: 16px 20px;
  overflow-y: auto;
}

.plan-detail dl {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0 18px;
  margin: 0;
}

.plan-detail dl div {
  padding: 10px 0;
  border-bottom: 1px solid #f0f3f6;
}

.plan-detail dt { color: #8a96a3; font-size: 10px; }
.plan-detail dd { margin: 4px 0 0; color: #344351; font-size: 12px; }

.plan-detail section {
  margin-top: 15px;
  padding: 14px;
  border-radius: 12px;
  background: #f7f9fb;
}

.plan-detail section h3 { margin: 0; font-size: 13px; }
.plan-detail section p { margin: 7px 0 0; color: #5e6c79; font-size: 12px; line-height: 1.7; white-space: pre-wrap; }

.plan-detail__progress { margin-top: 14px; }

.plan-detail footer {
  display: flex;
  justify-content: flex-end;
  padding: 12px 20px;
  border-top: 1px solid #edf0f3;
}

@media (max-width: 1100px) {
  .plan-stats { grid-template-columns: repeat(3, minmax(0, 1fr)); }
}

@media (max-width: 760px) {
  .plan-page { gap: 11px; }
  .plan-stats { grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 8px; }
  .plan-stat { padding: 12px; }
  .plan-stat strong { margin: 7px 0 5px; font-size: 21px; }
  .plan-card { padding: 12px; border-radius: 14px; }
  .plan-toolbar { align-items: stretch; }
  .plan-filters { flex-direction: column; }
  .plan-filters input,
  .plan-filters select { width: 100%; }
  .plan-grid { grid-template-columns: 1fr; }
  .plan-detail-mask { padding: 0; }
  .plan-detail {
    width: 100%;
    max-height: 100dvh;
    height: 100dvh;
    border-radius: 0;
  }
  .plan-detail header {
    position: sticky;
    top: 0;
    z-index: 2;
    padding: calc(10px + env(safe-area-inset-top)) 14px 13px;
    background: #ffffff;
  }
  .plan-detail__back {
    display: inline-flex;
    align-items: center;
    flex: 0 0 100%;
    min-height: 34px;
    padding: 0 2px;
    border: 0;
    background: transparent;
    color: #246347;
    font-size: 13px;
  }
  .plan-detail header h2 { font-size: 17px; }
  .plan-detail__body { padding: 12px 14px; }
  .plan-detail dl { grid-template-columns: 1fr; }
  .plan-detail footer {
    position: sticky;
    bottom: 0;
    padding: 10px 14px calc(10px + env(safe-area-inset-bottom));
    background: #ffffff;
  }
  .plan-detail footer button { width: 100%; }
}
</style>
